import { Component } from '@theme/component';
import { getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Sticky anchor nav that highlights the link matching the section currently
 * crossing a fixed offset from the top of the viewport (scroll-spy), and
 * smooth-scrolls to the target section on click.
 *
 * @typedef {Object} TrackerNavigationRefs
 * @property {HTMLAnchorElement[]} links
 *
 * @extends {Component<TrackerNavigationRefs>}
 */
class TrackerNavigationComponent extends Component {
  requiredRefs = ['links'];

  /** @type {number} */
  #offset = 100;

  /** @type {EventTarget | null} */
  #scrollTarget = null;

  /** @type {number | null} */
  #rafId = null;

  connectedCallback() {
    super.connectedCallback();

    this.#offset = Number(this.dataset.offset) || 100;
    this.#bindScroll();
    scrollContainerMediaQuery.addEventListener('change', this.#rebindScroll);
    window.addEventListener('resize', this.#handleScroll);

    this.#updateActiveLink();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#scrollTarget?.removeEventListener('scroll', this.#handleScroll);
    scrollContainerMediaQuery.removeEventListener('change', this.#rebindScroll);
    window.removeEventListener('resize', this.#handleScroll);

    if (this.#rafId !== null) cancelAnimationFrame(this.#rafId);
  }

  #bindScroll = () => {
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#handleScroll, { passive: true });
  };

  #rebindScroll = () => {
    this.#scrollTarget?.removeEventListener('scroll', this.#handleScroll);
    this.#bindScroll();
  };

  #handleScroll = () => {
    if (this.#rafId !== null) return;

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#updateActiveLink();
    });
  };

  #updateActiveLink() {
    const links = Array.isArray(this.refs.links) ? this.refs.links : [this.refs.links];

    const sections = links
      .map((link) => {
        const target = document.querySelector(link.getAttribute('href') ?? '');
        if (!target) return null;

        const rect = target.getBoundingClientRect();
        return { link, top: rect.top, bottom: rect.bottom };
      })
      .filter((section) => section !== null);

    // The section currently crossing the offset line wins; if none does (e.g. gaps
    // between sections), fall back to the first section still visible in the viewport.
    let active = sections.find((section) => section.top <= this.#offset && section.bottom > this.#offset);

    if (!active) {
      active = sections.find((section) => section.top <= window.innerHeight && section.bottom > 0);
    }

    for (const section of sections) {
      const isActive = section === active;
      section.link.classList.toggle('is-active', isActive);
      if (isActive) {
        section.link.setAttribute('aria-current', 'true');
      } else {
        section.link.removeAttribute('aria-current');
      }
    }
  }

  /**
   * @param {MouseEvent} event
   */
  handleLinkClick(event) {
    event.preventDefault();

    const href = event.target instanceof Element ? event.target.getAttribute('href') : null;
    if (!href) return;

    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

customElements.define('tracker-navigation-component', TrackerNavigationComponent);
